import { moveToFulfilled } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { FeedLoadAction } from '../../domains/feedLoad/actions.types';
import type {
  GroupFeedConsumptionStateBatchFulfilledAction,
  GroupFeedConsumptionStateFulfilledAction,
} from '../../domains/groupFeedConsumptionState/actions.types';
import type {
  MyGroupsLoadFulfilledAction,
  MyGroupsLoadMoreFulfilledAction,
} from '../../domains/myGroups/actions.types';
import type { PublishGroupMessageFulfilledAction } from '../../domains/publishers/actions.types';
import type {
  RegisterGroupVisitPendingAction,
  RegisterGroupVisitRejectedAction,
} from '../../domains/registerGroupVisit/actions.types';
import type { NormalizedThreadEntities } from '../../normalization/types';

import type { GroupFeedLastVisited, GroupFeedsLastVisited } from './types';

export type GroupFeedLastVisitedAction =
  | FeedLoadAction
  | PublishGroupMessageFulfilledAction
  | GroupFeedConsumptionStateFulfilledAction
  | GroupFeedConsumptionStateBatchFulfilledAction
  | MyGroupsLoadFulfilledAction
  | MyGroupsLoadMoreFulfilledAction
  | RegisterGroupVisitPendingAction
  | RegisterGroupVisitRejectedAction;

const initialState: GroupFeedsLastVisited = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const updateLastVisitedToNow = (state: GroupFeedsLastVisited, groupId: string, entities: NormalizedThreadEntities) => {
  const { messages } = entities;
  const newMessage = messages && Object.values(messages).length === 1 ? Object.values(messages)[0] : undefined;
  const hasLastVisited = Object.prototype.hasOwnProperty.call(state.fulfilled.byId, groupId);

  if (!hasLastVisited) {
    return state;
  }

  const lastVisited = state.fulfilled.byId[groupId];

  return moveToFulfilled(state, {
    [groupId]: {
      ...lastVisited,
      viewerLastVisitedAt: Date.now(),
      lastVisitMessageId: newMessage?.id,
      lastVisitThreadId: newMessage?.threadId,
    },
  });
};

const updateLastVisited = (
  state: GroupFeedsLastVisited,
  groupFeedsLastVisited?: Record<string, GroupFeedLastVisited>
) => {
  /* istanbul ignore next -- defensive guard against undefined entities */
  if (!groupFeedsLastVisited) {
    return state;
  }

  const updated = Object.entries(groupFeedsLastVisited).reduce<Record<string, GroupFeedLastVisited>>(
    (updatedById, [id, groupFeedLastVisited]) => {
      const hasExisting = Object.prototype.hasOwnProperty.call(state.fulfilled.byId, id);

      return !hasExisting || state.fulfilled.byId[id].viewerLastVisitedAt < groupFeedLastVisited.viewerLastVisitedAt
        ? { ...updatedById, [id]: groupFeedLastVisited }
        : updatedById;
    },
    {}
  );

  return Object.keys(updated).length > 0 ? moveToFulfilled(state, updated) : state;
};

const setViewerLastVisitedAt = (state: GroupFeedsLastVisited, groupId: string, viewerLastVisitedAt: number) => {
  const hasLastVisited = Object.prototype.hasOwnProperty.call(state.fulfilled.byId, groupId);

  if (!hasLastVisited) {
    return state;
  }

  const lastVisited = state.fulfilled.byId[groupId];

  return moveToFulfilled(state, {
    [groupId]: {
      ...lastVisited,
      viewerLastVisitedAt,
    },
  });
};

const reducer: Reducer<GroupFeedsLastVisited, GroupFeedLastVisitedAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FEED.LOAD_FULFILLED':
      return updateLastVisited(state, action.payload.entities.groupFeedsLastVisited);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return updateLastVisitedToNow(state, action.payload.groupId, action.payload.entities);
    case 'GROUP_FEED_CONSUMPTION_STATE.LOAD_FULFILLED':
      return updateLastVisited(state, action.payload.entities.groupFeedLastVisited);
    case 'GROUP_FEED_CONSUMPTION_STATE.LOAD_BATCH_FULFILLED':
      return updateLastVisited(state, action.payload.entities.groupFeedsLastVisited);
    case 'MY_GROUPS.LOAD_FULFILLED':
      return updateLastVisited(state, action.payload.entities.unseenThreads.groupFeedsLastVisited);
    case 'MY_GROUPS.LOAD_MORE_FULFILLED':
      return updateLastVisited(state, action.payload.entities.unseenThreads.groupFeedsLastVisited);
    case 'GROUP.REGISTER_VISIT_PENDING':
      return setViewerLastVisitedAt(state, action.payload.groupId, action.payload.optimisticVisitedAt);
    case 'GROUP.REGISTER_VISIT_REJECTED': {
      const { groupId, previousVisitedAt } = action.payload;

      return previousVisitedAt !== undefined ? setViewerLastVisitedAt(state, groupId, previousVisitedAt) : state;
    }
    default:
      return state;
  }
};

export default reducer;
